import { ScopeKey } from '@/core/enums/scopeKey';
import { StoreState } from '@/core/models/store.interface';
import { CancelToken } from 'axios';
import { Module } from 'vuex';
import { Company } from '../company/company.interface';
import { PropertyType } from '../property-type/property-type.interface';
import { TimetableWeek } from '../timetable-week/timetable-week.interface';
import { Board } from './board.interface';
import boardService from './board.service';

export interface BoardState {
   boards?: Array<Board>;
   board?: Board;
   board_loading: boolean;
   board_filter?: string;
   board_filter_loaded: boolean;
   board_scopes?: Array<ScopeKey>;
   current_hide_squads_without_shifts?: boolean;
}

const BoardStore: Module<BoardState, StoreState> = {
   namespaced: true,
   state: {
      board: undefined,
      board_filter: undefined,
      board_filter_loaded: true,
      board_scopes: undefined,
      current_hide_squads_without_shifts: undefined,
   },

   getters: {
      hasTimeTablesAndSquads: (state) => {
         return state.board?.timetables && state.board?.timetables?.length > 0 && state.board?.squads_count > 0;
      },
      getBoardFilterAsString(state) {
         if (!state.board_filter) return;

         return Object.entries(state.board_filter)
            .map((bf) => {
               let key = bf[0];
               let value = [];
               switch (key) {
                  case 'timetables':
                     key = 'timetable_ids';
                     break;
                  case 'board_companies':
                     key = 'board_company_ids';
                     break;
                  case 'squads':
                     key = 'squad_ids';
                     break;
                  case 'property_types':
                     key = 'property_ids';
                     break;
               }
               if (key !== 'property_ids') {
                  value = bf[1].filter((item) => item.checked).map((board_filter_group_item) => board_filter_group_item.id);
               } else {
                  const PROPERTY_VALUES = bf[1].map((board_filter_group_item) =>
                     board_filter_group_item.properties.filter((property) => property.checked).map((property) => property.id),
                  );

                  PROPERTY_VALUES.forEach((property_value) => value.push(...property_value));
               }
               return value.length > 0 ? `${key}=${value.join()}` : false;
            })
            .filter(Boolean)
            .join('&');
      },
      hasPermission: (state) => (scope_key: ScopeKey) => {
         return state.board_scopes?.includes(scope_key) ?? false;
      },
   },

   mutations: {
      SET_HIDE_SQUADS_WITHOUT_SHIFTS(state, new_hide_squads_without_shifts: boolean) {
         state.current_hide_squads_without_shifts = new_hide_squads_without_shifts;
      },
      REMOVE_BOARD_COMPANY(state, board_company: Company) {
         const INDEX_OF_COMPANY_TO_REMOVE = state.board?.companies?.findIndex((company) => company.id == board_company.id);
         if (INDEX_OF_COMPANY_TO_REMOVE && INDEX_OF_COMPANY_TO_REMOVE > -1) state.board?.companies?.splice(INDEX_OF_COMPANY_TO_REMOVE, 1);
      },
      UPDATE_BOARD_COMPANY(state, board_company) {
         const INDEX_OF_COMPANY_TO_UDPATE = state.board?.companies?.findIndex((company) => company.id == board_company.id);
         if (INDEX_OF_COMPANY_TO_UDPATE && !!state.board?.companies?.[INDEX_OF_COMPANY_TO_UDPATE])
            state.board.companies[INDEX_OF_COMPANY_TO_UDPATE] = board_company;
      },
      UPDATE_SHOW_SUM_SLOT_HOURS_PLANNED(state, { show_sum_slot_hours_planned }) {
         state.board['show_sum_slot_hours_planned'] = show_sum_slot_hours_planned;
      },
      setBoard(state, board) {
         state.board = board;
      },
      SET_BOARDS(state, boards) {
         state.boards = boards;
      },
      setBoardMargins(state, board_margins) {
         state.board!['minimum_seconds_between_shift'] = board_margins.minimum_seconds_between_shift;
      },
      setBoardSquads(state, board_squads) {
         if (!state.board?.squads) return;
         state.board.squads = board_squads;
      },
      updatePropertyTypeFromBoard(
         state,
         { property_type, update_properties = false }: { property_type: PropertyType; update_properties: boolean },
      ) {
         if (!state.board) return;
         if (!state.board?.property_types?.length) state.board.property_types = [];
         const INDEX_OF_PROPERTY_TYPE = (state.board?.property_types || []).findIndex((pt: { id: number }) => pt.id == property_type.id);
         if (INDEX_OF_PROPERTY_TYPE < 0) return;
         state.board.property_types[INDEX_OF_PROPERTY_TYPE] = {
            ...state.board.property_types,
            icon: property_type.icon,
            name: property_type.name,
            key: property_type.key,
            is_required: property_type.is_required,
            has_automatic_tags: property_type.has_automatic_tags,
            show_on_planning: property_type.show_on_planning,
            show_in_communication: property_type.show_in_communication,
         };

         if (update_properties) {
            state.board.property_types[INDEX_OF_PROPERTY_TYPE] = {
               ...state.board.property_types[INDEX_OF_PROPERTY_TYPE],
               properties: property_type.properties,
            };
         }
      },
      deletePropertyTypeFromBoard(state, { property_type_id }) {
         if (!state.board) return;
         state.board.property_types = state.board.property_types?.filter((property_type) => property_type.id != property_type_id);
      },
      addSquadToBoard(state, squad) {
         state.board?.squads?.splice(squad.sequence / 10 - 1, 0, squad);
         state.board?.squads?.map((squad, index) => {
            squad.sequence = (index + 1) * 10;
            return squad;
         });
      },
      deleteSquadFromBoard(state, squad_id) {
         if (state.board?.squads) state.board.squads = state.board?.squads?.filter((squad) => squad.id != squad_id);
      },
      addTimetableToBoard(state, timetable) {
         if (state.board?.timetables) state.board.timetables.push(timetable);
      },
      setPropertyType(state, property_type: PropertyType) {
         if (state.board?.property_types)
            state.board.property_types = state.board?.property_types?.map((board_property_type) =>
               board_property_type.id === property_type.id ? property_type : board_property_type,
            );
      },
      removeTimetableFromBoard(state, timetable_id: number) {
         const TIMETABLE_INDEX = (state.board?.timetables || []).findIndex((timetable) => timetable.id == timetable_id);
         if (TIMETABLE_INDEX >= 0) state.board?.timetables?.splice(TIMETABLE_INDEX, 1);
      },
      updateTimetableWeeks(
         state,
         { timetable_id, updated_timetable_weeks }: { timetable_id: number; updated_timetable_weeks: Array<TimetableWeek> },
      ) {
         if (!state.board?.timetables) return;
         if (!state.board.timetables.find((timetable) => timetable.id === timetable_id)?.timetable_weeks) return;

         state.board.timetables.find((timetable) => timetable.id === timetable_id)!.timetable_weeks = updated_timetable_weeks;
      },
      updateWeekInTimetable(state, { timetable_id, timetable_week_id, updated_timetable_week }) {
         const TIMETABLE_INDEX = (state.board?.timetables || []).findIndex((timetable) => timetable.id === timetable_id);
         if (TIMETABLE_INDEX <= -1) return;
         const TIMETABLE_WEEK_INDEX = state.board!.timetables![TIMETABLE_INDEX].timetable_weeks.findIndex(
            (timetable_week) => timetable_week.id === timetable_week_id,
         );
         if (TIMETABLE_WEEK_INDEX <= -1) return;

         state.board!.timetables![TIMETABLE_INDEX].timetable_weeks[TIMETABLE_WEEK_INDEX] = updated_timetable_week;
      },
      setBoardPermission(state, { accessor_type, accessor_id, is_allowed, scope, accessor_name, accessor_tenant }) {
         const permission_to_change = state.board?.permissions?.find(
            (p) => p.accessor.type == accessor_type && p.accessor.id == accessor_id && p.scope?.key == scope?.key,
         );
         if (permission_to_change && is_allowed != null) {
            permission_to_change.is_allowed = is_allowed;
         } else if (is_allowed != null) {
            state.board.permissions.push({
               accessor: { id: accessor_id, type: accessor_type, name: accessor_name, tenant: accessor_tenant },
               is_allowed: is_allowed,
               scope: scope,
            });
         } else {
            const index = state.board.permissions.findIndex(
               (p) => p.accessor.type == accessor_type && p.accessor.id == accessor_id && p.scope?.key == scope?.key,
            );
            state.board.permissions.splice(index, 1);
         }
      },
      setBoardPermissions(state, { permissions }) {
         if (!state.board) return;
         state.board.permissions = permissions;
      },
      addPoolsToBoard(state, pools) {
         pools.forEach((p) => {
            state.board.pools.push(p);
         });
      },
      updateTimetablePropertyInBoard(state, { timetable_id, path, value }) {
         state.board.timetables.find((tt) => tt.id == timetable_id)[path] = value;
      },
      updateTimetablePropertiesInBoard(state, { patches, updated_timetable }) {
         if (!state.board?.timetables) return;
         const TIMETABLE = state.board.timetables.find((tt) => tt?.id == updated_timetable?.id);
         if (!TIMETABLE) return;

         patches.forEach((patch) => {
            if (!patch?.path) return;
            TIMETABLE[patch.path] = patch.value;
         });
      },
      linkTagType(state, tag_type) {
         if (state.board) state.board.tag_types.push(tag_type);
      },
      removeTagTypeFromBoard(state, tag_type_id) {
         if (state.board) state.board.tag_types = state.board.tag_types.filter((tt) => tt.id != tag_type_id);
      },
      UPDATE_TAG_TYPE(state, tag_type) {
         if (!state.board) return;
         const TAG_TYPE_INDEX = state.board.tag_types.findIndex((tt) => tt.id == tag_type.id);
         state.board.tag_types[TAG_TYPE_INDEX] = tag_type;
      },
      SET_SCOPES(state, scopes: Array<ScopeKey>) {
         state.board_scopes = scopes;
      },
      SET_NEW_SHARE_AMOUNT(state, board_tenant) {
         state.board.board_tenants = board_tenant.board.board_tenants;
      },
      SET_NEW_CONTRACT_CREATION_TYPE(state, putted_board_tenant) {
         state.board.board_tenants.find((board_tenant) => board_tenant.id === putted_board_tenant.id).contract_creation_type =
            putted_board_tenant.contract_creation_type;
      },
      UPDATE_FIELD(state, { path, value }) {
         state.board[path] = value;
      },
      SET_BOARDS_CANDIDATE_POOLS_COUNT(state, { boards }) {
         state.boards?.forEach((board, index) => (board.candidate_pools_count = boards[index]?.candidate_pools_count));
      },
      UPDATE_POOL_COMPANY_AND_TEAM_AND_CONTRACT_DEFAULT_USER(
         state,
         { pool_id, contract_team, company, contract_creation_type, contract_default_user },
      ) {
         if (!state.board?.pools) return;
         const POOL_INDEX_TO_UPDATE = state.board.pools.findIndex((pool) => pool.id == pool_id);
         state.board.pools[POOL_INDEX_TO_UPDATE]['company'] = company;
         state.board.pools[POOL_INDEX_TO_UPDATE]['contract_team'] = contract_team;
         state.board.pools[POOL_INDEX_TO_UPDATE]['contract_team_id'] = contract_team?.id;
         state.board.pools[POOL_INDEX_TO_UPDATE]['contract_default_user'] = contract_default_user;
         state.board.pools[POOL_INDEX_TO_UPDATE]['contract_default_user_id'] = contract_default_user?.id;
         state.board.pools[POOL_INDEX_TO_UPDATE]['contract_creation_type'] = contract_creation_type;
         state.board.pools[POOL_INDEX_TO_UPDATE]['contract_creation_type_id'] = contract_creation_type?.id;
      },
   },

   actions: {
      async get(
         context,
         {
            page,
            search_value,
            include = 'tenants',
            sort,
            fields,
            cancelToken,
            per_page = 40,
            should_store = true,
         }: {
            page?: number;
            search_value?: string;
            include: string;
            sort?: string;
            fields?: string;
            cancelToken?: CancelToken;
            per_page: number;
            should_store: boolean;
         },
      ) {
         if (should_store) context.commit('SET_BOARDS', undefined);
         return await boardService.get({ page, search_value, include, sort, fields, cancelToken, per_page }).then((response) => {
            if (should_store) context.commit('SET_BOARDS', response?.data);
            return response;
         });
      },
      async getWithCandidatesCount(
         { commit },
         {
            page,
            search_value,
            sort,
            fields,
            include = 'candidate_poolsCount',
            cancelToken,
            per_page = 40,
         }: {
            page?: number;
            search_value?: string;
            sort?: string;
            fields?: string;
            include: string;
            cancelToken?: CancelToken;
            per_page: number;
         },
      ) {
         const BOARDS_RESPONSE = await boardService.get({ page, search_value, include, sort, fields, cancelToken, per_page });

         commit('SET_BOARDS_CANDIDATE_POOLS_COUNT', { boards: BOARDS_RESPONSE?.data });
      },
      async getBoardById(context, { board_id, append = '', space }: { board_id: number; append: string; space: boolean }) {
         if (!board_id || context.state.board?.id == board_id) return;
         context.commit('setBoard', undefined);

         return await boardService.getBoardById(board_id, append, space).then((response) => {
            if (!response) return;
            if (space && response) return response;

            context.commit('setBoard', response);
            return response;
         });
      },
      async fetchScopes(context, { board_id }: { board_id: number }) {
         if (context.state.board_scopes || !board_id) return;
         const SCOPES = await boardService.fetchScopes(board_id).then((response) => response?.scopes ?? []);
         context.commit('SET_SCOPES', SCOPES);
      },
      async put(
         context,
         {
            board_id,
            include,
            name,
            minimum_seconds_between_shift,
         }: { board_id: number; include: string; name: string; minimum_seconds_between_shift: number },
      ) {
         const BODY = { name, minimum_seconds_between_shift };
         return await boardService.put(board_id, include, BODY).then((response) => response);
      },
      async patch(context, { board_id, path, value }: { board_id: number; path: string; value: boolean }) {
         const BODY = [
            {
               op: 'replace',
               path,
               value,
            },
         ];

         return await boardService.patch(board_id, BODY);
      },
      async patches(context, { board_id, paths_values }: { board_id: number; paths_values: Array<{ path: string; value: any }> }) {
         if (paths_values?.length == 0) return;

         const BODY = [] as Array<{ op: string; path: string; value: any }>;
         paths_values.forEach((path_value) => {
            BODY.push({
               op: 'replace',
               path: path_value.path,
               value: path_value.value,
            });
         });

         return await boardService.patch(board_id, BODY);
      },
      async getNxtPaySettings(context, { board_id }: { board_id: number }) {
         return await boardService.getNxtPaySettings(board_id).then((response) => {
            if (!response) return;
            return response;
         });
      },
      async postNxtPaySettings(
         context,
         {
            board_id,
            type,
            property_type_id,
            companies,
         }: { board_id: number; type: string; property_type_id: number; companies: Array<Company> },
      ) {
         return await boardService.postNxtPaySettings(board_id, { type, property_type_id, companies }).then((response) => response);
      },
   },
};

export default BoardStore;
